import React, { useEffect, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import Menu from "@mui/icons-material/Menu";
import Dashboard from "@mui/icons-material/Dashboard";
import Business from "@mui/icons-material/Business";
import CreditCard from "@mui/icons-material/CreditCard";
import Subscriptions from "@mui/icons-material/Subscriptions";
import Payment from "@mui/icons-material/Payment";
import PersonAdd from "@mui/icons-material/PersonAdd";
import People from "@mui/icons-material/People";
import Logout from "@mui/icons-material/Logout";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { formatRoleForDisplay } from "../../Graphql/types/auth";
import { useLogoutAdmin } from "../../hooks/api/useAdminAuth";
import { ADMIN_CONTENT_MAX_WIDTH } from "../../styles/adminUiTokens";
import {
  ROUTE_HOME,
  ROUTE_MEMBERSHIP,
  ROUTE_SUBSCRIPTIONS,
  ROUTE_PAYMENTS,
  ROUTE_COMPANIES,
  ROUTE_ADD_ADMIN_USER,
  ROUTE_ADMIN_USERS,
  ROUTE_LOGIN,
} from "../../constants/routes";

const DRAWER_WIDTH = 260;
const APP_BAR_HEIGHT = 64;

const navItems = [
  { path: ROUTE_HOME, label: "Dashboard", icon: <Dashboard /> },
  { path: ROUTE_COMPANIES, label: "Companies", icon: <Business /> },
  { path: ROUTE_MEMBERSHIP, label: "Membership", icon: <CreditCard /> },
  {
    path: ROUTE_SUBSCRIPTIONS,
    label: "Subscriptions",
    icon: <Subscriptions />,
  },
  { path: ROUTE_PAYMENTS, label: "Payments", icon: <Payment /> },
];

const navItemSx = {
  borderRadius: 1.5,
  mb: 0.25,
  mx: 1,
  py: 1,
  "&.Mui-selected": {
    bgcolor: "action.selected",
    color: "primary.main",
    "& .MuiListItemIcon-root": { color: "primary.main" },
    "&:hover": { bgcolor: "action.selected" },
  },
};

const AdminLayout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAdminAuth();
  const logoutMutation = useLogoutAdmin();

  useEffect(() => {
    setDrawerOpen(!isMobile);
  }, [isMobile]);

  const handleLogout = () => {
    logoutMutation.mutate(undefined as unknown as void);
    navigate(ROUTE_LOGIN);
  };

  const drawerContent = (
    <>
      <List sx={{ pt: 1.5, px: 0, flex: 1 }}>
        {navItems.map((item) => (
          <ListItemButton
            key={item.path}
            selected={location.pathname === item.path}
            onClick={() => {
              navigate(item.path);
              if (isMobile) setDrawerOpen(false);
            }}
            sx={navItemSx}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontWeight: 500,
                fontSize: "0.9375rem",
              }}
            />
          </ListItemButton>
        ))}
        <ListItemButton
          selected={location.pathname.startsWith(ROUTE_ADMIN_USERS)}
          onClick={() => {
            navigate(ROUTE_ADMIN_USERS);
            if (isMobile) setDrawerOpen(false);
          }}
          sx={navItemSx}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <People />
          </ListItemIcon>
          <ListItemText
            primary="Admin Users"
            primaryTypographyProps={{ fontWeight: 500, fontSize: "0.9375rem" }}
          />
        </ListItemButton>
        <ListItemButton
          onClick={() => {
            navigate(ROUTE_ADD_ADMIN_USER);
            if (isMobile) setDrawerOpen(false);
          }}
          sx={{ ...navItemSx, color: "text.secondary" }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <PersonAdd />
          </ListItemIcon>
          <ListItemText
            primary="Add Admin User"
            primaryTypographyProps={{ fontWeight: 500, fontSize: "0.9375rem" }}
          />
        </ListItemButton>
      </List>
      <List sx={{ px: 0, pb: 1.5 }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{ borderRadius: 1.5, mx: 1, color: "error.main" }}
          disabled={logoutMutation.isPending}
        >
          <ListItemIcon sx={{ minWidth: 40, color: "inherit" }}>
            <Logout />
          </ListItemIcon>
          <ListItemText primary="Log out" />
        </ListItemButton>
      </List>
    </>
  );

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        minHeight: "100vh",
        bgcolor: "background.default",
        boxSizing: "border-box",
      }}
    >
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          bgcolor: "background.paper",
          color: "text.primary",
          borderBottom: "1px solid",
          borderColor: "divider",
          height: APP_BAR_HEIGHT,
        }}
      >
        <Toolbar
          sx={{
            minHeight: `${APP_BAR_HEIGHT}px !important`,
            px: { xs: 1.5, sm: 2 },
          }}
        >
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setDrawerOpen((open) => !open)}
            aria-label="Toggle navigation"
            sx={{ mr: 1 }}
          >
            <Menu />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            fontWeight={700}
            sx={{ fontSize: { xs: "1rem", sm: "1.125rem" } }}
          >
            Kivunova FMS Admin
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ display: { xs: "none", sm: "block" } }}
          >
            {user ? formatRoleForDisplay(user.role) : ""}
          </Typography>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isMobile ? "temporary" : "persistent"}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          // Temporary drawer overlays; persistent drawer reserves width in flex flow.
          width: isMobile ? 0 : drawerOpen ? DRAWER_WIDTH : 0,
          flexShrink: 0,
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            top: APP_BAR_HEIGHT,
            height: `calc(100% - ${APP_BAR_HEIGHT}px)`,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          boxSizing: "border-box",
          mt: `${APP_BAR_HEIGHT}px`,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: ADMIN_CONTENT_MAX_WIDTH,
            mx: "auto",
            px: { xs: 1.5, sm: 2.5, md: 3 },
            py: { xs: 2, sm: 3 },
            boxSizing: "border-box",
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;
